import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';

interface ArticleCardProps {
  id: number;
  title: string;
  slug: string;
  category: string;
  excerpt: string;
  image: string;
  author: string;
  publishedAt: string;
  views: number;
  comments: number;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({
  id,
  title,
  slug,
  category,
  excerpt,
  image,
  author,
  publishedAt,
  views,
  comments,
  variant = 'default',
}: ArticleCardProps) {
  const { t } = useTranslation();
  const cat = categories.find((c) => c.slug === category);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return '刚刚';
    if (diffHours < 24) return `${diffHours}小时前`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}天前`;
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block relative rounded-2xl overflow-hidden h-[400px] cursor-pointer"
      >
        <img
          src={image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {cat && (
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium mb-3">
              {cat.name}
            </span>
          )}
          <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 line-clamp-2 group-hover:text-primary-200 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-white/70 line-clamp-2 mb-3">{excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>{author}</span>
            <span>{formatDate(publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {comments}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${id}`}
        className="group flex gap-4 p-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{formatDate(publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="group block bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {cat && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium backdrop-blur-sm">
            {cat.name}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-base font-bold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span>{author}</span>
            <span>{formatDate(publishedAt)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {comments}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}